import { useState } from 'react';
import { User, Mic, CheckCircle, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useTeamProfilesForInterview, useTeamProfile } from '@/hooks/useTeamProfileInterview';
import { INTERVIEW_CATEGORIES, TOTAL_QUESTIONS } from '@/types/team-profiles';
import type { InterviewQuestion, InterviewResponse } from '@/types/team-profiles';
import { InterviewQuestionModal } from '@/components/team-profile/InterviewQuestionModal';

export default function TeamProfileInterview() {
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<InterviewQuestion | null>(null);

  const { data: profiles = [], isLoading: profilesLoading } = useTeamProfilesForInterview();
  const { data: selectedProfile } = useTeamProfile(selectedProfileId);

  // Get answered questions from profile
  const interviewData = selectedProfile?.interview_data;
  const answeredResponses: InterviewResponse[] = interviewData?.responses || [];
  const answeredIds = new Set(answeredResponses.map((r) => r.question_id));
  const completedCount = answeredIds.size;
  const progressPercentage = TOTAL_QUESTIONS > 0 ? (completedCount / TOTAL_QUESTIONS) * 100 : 0;

  const getEstimatedTimeRemaining = () => {
    const remaining = TOTAL_QUESTIONS - completedCount;
    if (remaining === 0) return "Complete!";
    const totalMinutes = remaining * 5;
    if (totalMinutes < 60) return `~${totalMinutes} min remaining`;
    return `~${Math.round(totalMinutes / 60)} hr remaining`;
  };

  const getQuestionResponse = (questionId: number): InterviewResponse | undefined => {
    return answeredResponses.find((r) => r.question_id === questionId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Team Profile Interview</h1>
        <p className="text-muted-foreground">
          Answer these questions to help the AI generate more authentic LinkedIn content in your voice.
        </p>
      </div>

      {/* Profile Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-4 w-4" />
            Select Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Team Member</Label>
              <Select
                value={selectedProfileId || ''}
                onValueChange={(v) => setSelectedProfileId(v || null)}
                disabled={profilesLoading}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder={profilesLoading ? "Loading team..." : "Select your name..."} />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      <div className="flex flex-col">
                        <span>{profile.full_name}</span>
                        {profile.job_title && (
                          <span className="text-xs text-muted-foreground">
                            {profile.job_title}{profile.territory ? ` \u2022 ${profile.territory}` : ''}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedProfile && (
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{selectedProfile.full_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedProfile.job_title || 'No title'} {selectedProfile.territory ? `\u2022 ${selectedProfile.territory}` : ''}
                  </div>
                </div>
                {selectedProfile.interview_completed_at && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Interview Complete
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      {selectedProfile && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold">{Math.round(progressPercentage)}%</div>
                  <div className="text-muted-foreground">Complete</div>
                </div>
                <div className="text-right space-y-1">
                  <div className="flex items-center gap-2 text-sm justify-end">
                    <span>{completedCount} of {TOTAL_QUESTIONS} questions answered</span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground justify-end">
                    <span>{getEstimatedTimeRemaining()}</span>
                    <Clock className="h-4 w-4" />
                  </div>
                </div>
              </div>
              <Progress value={progressPercentage} className="h-3" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interview Questions by Category */}
      {selectedProfile && (
        <div className="space-y-4">
          {INTERVIEW_CATEGORIES.map((category) => (
            <Card key={category.id}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span>{category.emoji}</span>
                  {category.label}
                </CardTitle>
                <CardDescription>
                  {category.questions.filter((q) => answeredIds.has(q.id)).length} of {category.questions.length} answered
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {category.questions.map((question) => {
                    const response = getQuestionResponse(question.id);
                    const isAnswered = !!response;

                    return (
                      <button
                        key={question.id}
                        onClick={() => setSelectedQuestion(question)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors hover:bg-muted/50 ${
                          isAnswered ? 'border-green-200 bg-green-50/50' : 'border-border'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div
                              className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                                isAnswered
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {isAnswered ? <CheckCircle className="h-3.5 w-3.5" /> : question.id}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">
                                {question.emoji} {question.text}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {question.subtext}
                                {isAnswered && response?.answered_at && (
                                  <span className="ml-2 text-green-600">
                                    &bull; Answered {new Date(response.answered_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
                        </div>
                        {isAnswered && response?.response_text && (
                          <div className="mt-2 ml-10 text-xs text-muted-foreground line-clamp-2">
                            {response.response_text}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No Profile Selected */}
      {!selectedProfile && !profilesLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Mic className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Select Your Profile</h3>
            <p className="text-muted-foreground">
              Choose your name above to view and complete your interview questions.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Question Modal */}
      {selectedQuestion && selectedProfile && (
        <InterviewQuestionModal
          isOpen={true}
          onClose={() => setSelectedQuestion(null)}
          question={selectedQuestion}
          profileId={selectedProfile.id}
          existingResponse={getQuestionResponse(selectedQuestion.id)}
        />
      )}
    </div>
  );
}
